import React from 'react';
import { motion } from 'framer-motion';
import { Bitcoin, TrendingUp, Shield, Zap, Globe, BarChart3 } from 'lucide-react';

// Import professional crypto trading image
const cryptoTradingImage = require('../../assets/businesswoman-bitcoin.jpg');
const futuristicTradingImage = require('../../assets/business-person-futuristic-business-environment.jpg');

const CryptoTrading = () => {
  const cryptoFeatures = [
    {
      icon: <Bitcoin className="h-8 w-8 text-orange-600" />,
      title: '100+ Cryptocurrencies',
      description: 'Trade Bitcoin, Ethereum, and hundreds of other cryptocurrencies with competitive spreads.',
      benefits: ['Bitcoin & Ethereum', 'DeFi tokens', 'Stablecoins', 'Altcoins']
    },
    {
      icon: <Shield className="h-8 w-8 text-blue-600" />,
      title: 'Secure Storage',
      description: 'Your crypto assets are protected with industry-leading security and cold storage.',
      benefits: ['Cold storage wallets', 'Multi-signature security', 'Insurance coverage', '24/7 monitoring']
    },
    {
      icon: <Zap className="h-8 w-8 text-green-600" />,
      title: 'Instant Trading',
      description: 'Execute crypto trades instantly with our high-performance trading engine.',
      benefits: ['Real-time execution', 'Low latency', 'Advanced order types', 'API access']
    },
    {
      icon: <BarChart3 className="h-8 w-8 text-purple-600" />,
      title: 'Advanced Analytics',
      description: 'Professional charting tools and market analysis for cryptocurrency trading.',
      benefits: ['Technical indicators', 'Market depth', 'Trading signals', 'Portfolio tracking']
    }
  ];

  const popularCryptos = [
    { symbol: 'BTC', name: 'Bitcoin', price: '$43,567', change: '+1.28%', marketCap: '$850B' },
    { symbol: 'ETH', name: 'Ethereum', price: '$2,543', change: '+3.45%', marketCap: '$305B' },
    { symbol: 'BNB', name: 'Binance Coin', price: '$312.45', change: '-2.15%', marketCap: '$48B' },
    { symbol: 'ADA', name: 'Cardano', price: '$0.48', change: '+5.67%', marketCap: '$17B' },
    { symbol: 'SOL', name: 'Solana', price: '$89.23', change: '+7.34%', marketCap: '$38B' },
    { symbol: 'DOT', name: 'Polkadot', price: '$6.78', change: '+2.89%', marketCap: '$8B' }
  ];

  return (
    <div className="crypto-trading-page">
      {/* Hero Section */}
      <section className="crypto-trading-hero">
        <div className="crypto-trading-hero-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="crypto-trading-hero-content"
            >
              <div className="crypto-trading-hero-badge">
                <Bitcoin className="crypto-trading-hero-badge-icon" />
                <span>Cryptocurrency Trading</span>
              </div>
              <h1 className="crypto-trading-hero-title">
                Trade Crypto with
                <span className="crypto-trading-hero-title-gradient"> Professional Tools</span>
              </h1>
              <p className="crypto-trading-hero-subtitle">
                Access 100+ cryptocurrencies with secure storage, instant trading, and professional-grade 
                analytics. Start your crypto journey with the most trusted platform.
              </p>
              <div className="crypto-trading-hero-buttons">
                <motion.a
                  href="https://dashboard.chartlinebrokerage.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="crypto-trading-hero-cta-primary"
                >
                  Start Crypto Trading
                </motion.a>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="crypto-trading-hero-cta-secondary"
                >
                  Explore Coins
                </motion.button>
              </div>
            </motion.div>

            {/* Right Image */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="crypto-trading-hero-image"
            >
              <div className="relative">
                <img
                  src={cryptoTradingImage}
                  alt="Cryptocurrency Trading"
                  className="w-full h-auto rounded-2xl shadow-2xl"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-orange-900/20 to-transparent rounded-2xl" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="crypto-trading-features-section">
        <div className="crypto-trading-features-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="crypto-trading-features-header"
          >
            <h2 className="crypto-trading-features-title">
              Advanced Cryptocurrency Trading Platform
            </h2>
            <p className="crypto-trading-features-subtitle">
              Trade with confidence using our secure, fast, and feature-rich cryptocurrency platform.
            </p>
          </motion.div>

          <div className="crypto-trading-features-grid">
            {cryptoFeatures.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="crypto-trading-feature-card"
              >
                <div className="crypto-trading-feature-icon">
                  {feature.icon}
                </div>
                <h3 className="crypto-trading-feature-title">{feature.title}</h3>
                <p className="crypto-trading-feature-description">{feature.description}</p>
                <ul className="crypto-trading-feature-benefits">
                  {feature.benefits.map((benefit, idx) => (
                    <li key={idx} className="crypto-trading-feature-benefit">
                      <div className="crypto-trading-feature-check" />
                      {benefit}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Popular Cryptos Section */}
      <section className="crypto-trading-cryptos-section">
        <div className="crypto-trading-cryptos-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="crypto-trading-cryptos-header"
          >
            <h2 className="crypto-trading-cryptos-title">Popular Cryptocurrencies</h2>
            <p className="crypto-trading-cryptos-subtitle">
              Trade the most popular cryptocurrencies with competitive spreads
            </p>
          </motion.div>

          <div className="crypto-trading-cryptos-grid">
            {popularCryptos.map((crypto, index) => (
              <motion.div
                key={crypto.symbol}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="crypto-trading-crypto-card"
              >
                <div className="crypto-trading-crypto-header">
                  <div className="crypto-trading-crypto-symbol">{crypto.symbol}</div>
                  <div className={`crypto-trading-crypto-change ${crypto.change.startsWith('+') ? 'positive' : 'negative'}`}>
                    {crypto.change}
                  </div>
                </div>
                <div className="crypto-trading-crypto-name">{crypto.name}</div>
                <div className="crypto-trading-crypto-price">{crypto.price}</div>
                <div className="crypto-trading-crypto-marketcap">Market Cap: {crypto.marketCap}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Security Section */}
      <section className="crypto-trading-security-section">
        <div className="crypto-trading-security-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            
            {/* Left Image */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="crypto-trading-security-image"
            >
              <img
                src={futuristicTradingImage}
                alt="Secure Crypto Trading"
                className="w-full h-auto rounded-2xl shadow-xl"
              />
            </motion.div>

            {/* Right Content */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="crypto-trading-security-content"
            >
              <h2 className="crypto-trading-security-title">
                Bank-Grade <span className="text-orange-600">Security</span>
              </h2>
              <p className="crypto-trading-security-subtitle">
                Your cryptocurrency investments are protected with the highest level of security measures.
              </p>
              
              <div className="crypto-trading-security-features">
                <div className="crypto-trading-security-feature">
                  <Shield className="crypto-trading-security-feature-icon" />
                  <div>
                    <h4>Cold Storage Protection</h4>
                    <p>95% of crypto assets stored offline in secure cold wallets</p>
                  </div>
                </div>
                <div className="crypto-trading-security-feature">
                  <Globe className="crypto-trading-security-feature-icon" />
                  <div>
                    <h4>Multi-Signature Wallets</h4>
                    <p>Multiple authorization required for enhanced security</p>
                  </div>
                </div>
                <div className="crypto-trading-security-feature">
                  <TrendingUp className="crypto-trading-security-feature-icon" />
                  <div>
                    <h4>Insurance Coverage</h4>
                    <p>Your crypto assets are insured against security breaches</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="crypto-trading-cta-section">
        <div className="crypto-trading-cta-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="crypto-trading-cta-content"
          >
            <h2 className="crypto-trading-cta-title">
              Ready to Enter the Crypto Market?
            </h2>
            <p className="crypto-trading-cta-subtitle">
              Join the cryptocurrency revolution with ChartLine's secure and professional trading platform. 
              Start trading crypto today with industry-leading security and support.
            </p>
            <motion.a
              href="https://dashboard.chartlinebrokerage.com"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="crypto-trading-cta-button"
            >
              Start Crypto Trading
            </motion.a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default CryptoTrading; 